/**
 * The Shop, and the Stash it feeds (2026-09-02).
 *
 * User: "work on the visual design for the shop and all items (combining the
 * vendor and cosmetics into a single 'Shop'). We also need to visualize the
 * stash."
 *
 * ## Why one Shop
 *
 * Vendor and Cosmetics were two hub tiles, two panels and two mental models
 * for the same verb. The only real difference between them is which currency
 * they take — Wax for curios and satchel charges, Ember for skins and emotes
 * — and a currency is a detail of a purchase, not a reason to hide half the
 * things you can buy behind a different door. One Shop, four tabs, and the
 * price on each card says which currency it wants.
 *
 * **Selling stays in the Stash — for loot.** You sell curios where curios
 * live, and putting a second sell list in the Shop would mean two screens
 * both claiming to be where your bag is.
 *
 * **Amended 2026-09-04.** User: "All these items and skins should be
 * available not only for purchase, but also for sale." Arms, gear and
 * cosmetics are not in the bag — they are entitlements, and the Stash has
 * never listed one. So they are sold from the shelf that sold them, where the
 * card already knows the price, the name and the picture. The rule underneath
 * did not change: **a thing is sold where it is kept**, and the Shop is where
 * these are kept.
 *
 * ## Why the stash is a grid and not a list
 *
 * A stash is scanned ("do I have anything worth selling?"), not read
 * top-to-bottom, and it is capacity-bounded — which a list of rows cannot
 * show you. A grid with a fill meter answers both questions before you read
 * a single label.
 *
 * Pure string builders, for the same reasons as `map-picker.ts`: the hub
 * renders panels with `innerHTML`, and the interesting states here — owned,
 * equipped, unaffordable, empty, full — are all worth a test that does not
 * need a hub or a server.
 */

import type { LootRarity } from '../config/item-defs';
import type { CosmeticDefRow } from '../net/cosmetics-api';
import type { ConsumableDefRow, ConsumableEffect } from '../net/consumables-api';
import type { RiteDefRow } from '../net/rites-api';
import {
  itemCardMarkup, itemTileMarkup, itemIconSrc, cosmeticIconSrc, consumableIconSrc,
  itemSubtitle, itemTitle, rarityTextClass, esc, riteIconSrc,
 UNKNOWN_PLATE_SRC,} from './item-icon';
import { amountMarkup, slotsMarkup, currencyIcon } from './currency';
import { getIntlLocale, N_, t, tp } from '../i18n/i18n';
import { classDisplayName } from './class-picker';

/**
 * The Shop's shelves.
 *
 * **Curios left on 2026-09-04.** It sold you loot back at a markup, and a
 * curio has exactly one verb — Sell — so the shelf was a 3x-to-15x loss on an
 * object that does nothing while you hold it. What curios are actually for
 * now lives on the commission board (`ui/commissions.ts`), which pays *above*
 * the vendor for specific ones. Selling still happens in the Stash, where the
 * things you own already live.
 */
export const SHOP_TABS = [
  { id: 'satchel', label: N_('Satchel') },
  // Arms before Gear: the two are read as a pair — what you hold, then what
  // you wear — and the satchel above them is the third thing you take down.
  { id: 'arms', label: N_('Arms') },
  { id: 'gear', label: N_('Gear') },
  { id: 'cosmetics', label: N_('Cosmetics') },
  { id: 'energy', label: N_('Energy') },
  { id: 'ember', label: N_('Ember') },
  { id: 'wax', label: N_('Wax') },
] as const;

export type ShopTabId = (typeof SHOP_TABS)[number]['id'];

/** Horizontal strip; scrolls rather than wraps on a narrow phone, so the row never becomes two. */
export function shopTabsMarkup(active: ShopTabId): string {
  const tabs = SHOP_TABS.map((tab) => {
    const on = tab.id === active;
    const cls = on
      ? 'btn btn-stone-on shrink-0 px-2.5 font-heading tracking-[0.06em]'
      : 'btn btn-stone shrink-0 px-2.5 font-heading tracking-[0.06em]';
    return `<button data-shop-tab="${tab.id}" class="${cls}" aria-selected="${on}" role="tab">${esc(t(tab.label))}</button>`;
  }).join('');
  return `<div role="tablist" class="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 -mx-1 px-1">${tabs}</div>`;
}

/** A grid, not a list — see the file header. Two columns on a phone, four on a desktop panel. */
export function gridMarkup(cards: string[], empty: string): string {
  if (cards.length === 0) return `<p class="text-[13px] text-text-dim">${esc(empty)}</p>`;
  return `<div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">${cards.join('')}</div>`;
}

/**
 * `labelMarkup` is markup, not text: a price is a glyph plus a numeral plus a
 * word, and escaping it here would print the SVG. Every caller builds it with
 * `amountMarkup`, which does its own escaping of the parts that need it.
 */
function priceButton(dataAttr: string, id: string, labelMarkup: string, affordable: boolean): string {
  const cls = affordable
    ? 'btn btn-stone w-full px-2 text-[12px]'
    : 'btn btn-stone w-full px-2 text-[12px]';
  const disabled = affordable ? '' : ' disabled';
  return `<button ${dataAttr}="${esc(id)}" class="${cls}"${disabled}>${labelMarkup}</button>`;
}

/**
 * The other side of `priceButton` (2026-09-04).
 *
 * Deliberately quieter than a Buy: it sits on an owned card, where the player
 * has already got what they came for, and a control shouting for a click on
 * something you already own is a control trying to sell you a mistake. The
 * word comes first and the number second — you press this because you decided
 * to be rid of the thing, not because the quote tempted you.
 *
 * Every caller pairs it with a confirm, because none of these can be undone
 * for free and two of them cannot be undone at all.
 */
/**
 * Equip beside Sell, stacking when the card is too narrow for both (i18n,
 * 2026-10-16): "Надеть" + "Продать 1 234" overflowed a 375px card in ru/de.
 */
const EQUIP_SELL_ROW = 'flex flex-wrap gap-1.5 [&>*]:flex-1 [&>*]:min-w-[6.5rem]';

function sellButton(dataAttr: string, id: string, amount: string, opts: { title?: string } = {}): string {
  const title = opts.title ? ` title="${esc(opts.title)}"` : '';
  return `<button ${dataAttr}="${esc(id)}" class="btn btn-stone w-full px-2 text-[12px]"${title}>`
    + `<span class="inline-flex flex-wrap items-center justify-center gap-x-1"><span class="text-text-dim">${esc(t('Sell'))}</span>${amount}</span></button>`;
}

/**
 * One stash item.
 *
 * The sell price is on the button rather than in the subtitle, because it is
 * the number you act on — and the weight is in the subtitle, because it is
 * the number that decides whether you carry it out next time.
 */
export function stashCardMarkup(
  item: { id: string; itemDefId: string; rarity: LootRarity },
  sellWax: number,
  /**
   * How many identical rows this card stands for (2026-09-05).
   *
   * The Stash panel and the inventory window draw the same rows, and before
   * this the panel listed three Gilded Reliquaries as three identical cards
   * while the window had learned to draw them as one. Two screens disagreeing
   * about the same data is worse than either answer on its own.
   *
   * The grouping is `inventory-window.ts`'s `stackStash`; Sell still sells one
   * row, which is why the button keeps the unit price beside a count.
   */
  count = 1,
): string {
  return itemCardMarkup({
    src: itemIconSrc(item.itemDefId),
    alt: itemTitle(item.itemDefId),
    rarity: item.rarity,
    detailAttr: `data-detail-curio="${esc(item.itemDefId)}"`,
    // Plain text, not markup: `itemCardMarkup` escapes its title, so a span in
    // here prints its own tags.
    title: count > 1 ? `${itemTitle(item.itemDefId)} \u00d7${count.toLocaleString(getIntlLocale())}` : itemTitle(item.itemDefId),
    subtitle: itemSubtitle(item.itemDefId, item.rarity),
    action: priceButton('data-sell', item.id, `<span class="text-text-dim">${esc(count > 1 ? t('Sell one') : t('Sell'))}</span>${amountMarkup('wax', sellWax, { hideLabel: true })}`, true),
  });
}

/**
 * How full the stash is, as a bar.
 *
 * A stash that is one item from full and a stash that is half empty are
 * different situations, and "12/25" alone makes you do the arithmetic. Turns
 * amber near the cap and red at it, because being full is the state that
 * silently costs you loot at the end of a run.
 */
export function stashCapacityMarkup(used: number, capacity: number): string {
  const safeCapacity = capacity > 0 ? capacity : 0;
  const safeUsed = Math.max(0, used);
  const pct = safeCapacity > 0 ? Math.min(100, (safeUsed / safeCapacity) * 100) : 0;
  const full = safeCapacity > 0 && safeUsed >= safeCapacity;
  const near = !full && pct >= 80;
  const tint = full ? 'bg-error' : near ? 'bg-warn' : 'bg-success';
  const note = full ? esc(t('Full — sell something before your next run')) : '';
  return `
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between gap-2">
        <span class="text-[12px] text-text-dim">${esc(t('Stash'))}</span>
        <span class="text-[12px] ${full ? 'text-error' : ''}">${slotsMarkup(safeUsed, safeCapacity, { hideLabel: true })}</span>
      </div>
      <div class="meter h-2.5">
        <div class="meter-fill absolute inset-y-0 left-0 ${tint}" style="width:${pct}%"></div>
      </div>
      ${note ? `<p class="text-[11px] text-error">${note}</p>` : ''}
    </div>
  `;
}

export interface CosmeticCardState {
  owned: boolean;
  equipped: boolean;
  affordable: boolean;
  /**
   * What the vendor pays to take it back, from the owned row rather than the
   * catalogue: the refund is priced against what this player actually paid, so
   * a later price change never rewrites their receipt. Undefined draws no Sell
   * button at all, which is the right answer for a server that predates the
   * buyback counter.
   */
  buybackEmber?: number;
}

/**
 * A cosmetic's shelf name.
 *
 * Reads the server's `name` first and only prettifies the id when there is
 * none. The prettified form is what the shop actually showed until 2026-09-04
 * — `knight_gilded` rendered as "Knight Gilded", which reads as a database key
 * that got out — so it survives as a fallback for an older server, not as the
 * intended output.
 */
export function cosmeticLabel(cosmeticDefId: string, name?: string): string {
  if (name) return t(name);
  return cosmeticDefId.replace(/^emote_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A skin or an emote.
 *
 * Owned-and-equipped shows a status pill rather than a button, matching the
 * map picker's "Selected" treatment — there is nothing to press on the thing
 * you already chose.
 */
export function cosmeticCardMarkup(def: CosmeticDefRow, state: CosmeticCardState): string {
  const isEmote = def.kind === 'EMOTE';
  const label = cosmeticLabel(def.id, def.name);
  // "unlimited" is the word doing the work here — see `shelfHeadingMarkup` on
  // why an emote was being read as something that runs out.
  const subtitle = isEmote
    ? t('Emote · any delver, unlimited')
    : t('Skin · {className}', { className: classDisplayName(String(def.appliesToClass)) });
  let action: string;
  if (state.owned) {
    /**
     * **The shop stopped dressing you (2026-09-07.)** User: *"Skins and emojis
     * should be equiped here. Not in shop for sure."*
     *
     * An owned card used to carry Equip / Equipped, which made the Cosmetics
     * tab the only shelf in the game that was also a wardrobe — arms and gear
     * are bought here and worn on the paperdoll, and a skin should be no
     * different. Equipping moved to the Inventory window's Look section.
     *
     * What is left is what a shop is for: it says you own it, and it offers to
     * buy it back. "Worn" rides along as a fact rather than a control, because
     * knowing which skin is on your delver is worth having at the till — it is
     * the difference between buying a second one and buying a duplicate.
     */
    const state_line = state.equipped
      ? `<span class="btn w-full px-2 text-[12px] text-gold pointer-events-none">${esc(t('Owned · worn'))}</span>`
      : isEmote
        ? `<span class="btn w-full px-2 text-[12px] text-gold pointer-events-none">${esc(t('Owned for good'))}</span>`
        : `<span class="btn w-full px-2 text-[12px] text-gold pointer-events-none">${esc(t('Owned'))}</span>`;
    // A missing quote hides the button rather than guessing at one: the refund
    // is priced against what this player paid, which only the server knows.
    const sell = state.buybackEmber === undefined ? '' : sellButton(
      'data-sell-cosmetic', def.id, amountMarkup('ember', state.buybackEmber, { hideLabel: true }),
      { title: t('Sell back for {refund} Ember. You can buy it again for {price}.', {
        refund: groupNumber(state.buybackEmber), price: groupNumber(def.priceEmber),
      }) },
    );
    action = sell ? `<div class="flex flex-col gap-1.5">${state_line}${sell}</div>` : state_line;
  } else {
    action = priceButton('data-buy-cosmetic', def.id, amountMarkup('ember', def.priceEmber), state.affordable);
  }
  return itemCardMarkup({
    src: cosmeticIconSrc(def.id),
    alt: label,
    rarity: 'RARE', // every cosmetic is Ember-priced; the gold frame says so
    muted: !state.owned && !state.affordable,
    detailAttr: `data-detail-cosmetic="${esc(def.id)}"`,
    title: label,
    subtitle,
    action,
  });
}

/** A consumable on the satchel shelf, showing what you own and what this delver will carry. */
export function consumableCardMarkup(
  def: ConsumableDefRow,
  opts: { owned: number; taking: number; effectLine: string; affordable: boolean },
): string {
  const owned = opts.taking > 0
    ? t('{owned} owned · taking {taking}', { owned: opts.owned, taking: opts.taking })
    : t('{owned} owned', { owned: opts.owned });
  const name = t(def.name);
  return `
    <div class="frame-inset flex flex-col gap-1.5 p-2">
      ${itemTileMarkup({
        src: consumableIconSrc(def.id),
        alt: name,
        muted: !opts.affordable && opts.owned === 0,
        badge: opts.owned > 0 ? String(opts.owned) : undefined,
        detailAttr: `data-detail-consumable="${esc(def.id)}"`,
      })}
      <div class="min-w-0">
        <p class="text-[13px] truncate">${esc(name)}</p>
        <p class="text-[11px] text-text-dim truncate">${esc(opts.effectLine)}</p>
        <p class="text-[11px] ${opts.owned > 0 ? 'text-gold' : 'text-text-dim'}">${esc(owned)}</p>
      </div>
      ${priceButton('data-buy-consumable', def.id, amountMarkup('wax', def.priceWax), opts.affordable)}
    </div>
  `;
}

/**
 * One Rite on the shop shelf (2026-09-07) — design/gdd/rites.md.
 *
 * Two things this card does that `consumableCardMarkup` does not, and both
 * follow from a Rite being one-per-run rather than a stack of charges:
 *
 * **It shows what is selected, not just what is owned.** A satchel charge you
 * own is a charge you will carry; a Rite you own is a Rite you *might* burn,
 * and exactly one of them will be. So the selected card carries a mark and the
 * others do not, and the Select control is on the card rather than only on a
 * separate picker — the moment you are most likely to want to choose one is
 * the moment you have just bought it.
 *
 * **The condition is on the face of it.** Every Rite is a wager, and which
 * wager is the whole difference between the four XP Rites. A card that showed
 * "+25% XP" without "only if you extract" would be selling a different item
 * from the one in the table.
 */
export function riteCardMarkup(
  def: RiteDefRow,
  opts: { owned: number; selected: boolean; effectLine: string; affordable: boolean },
): string {
  // Written out per branch, never interpolated: Tailwind v4 extracts class
  // names statically, so a template hole inside a class attribute produces no
  // CSS rule at all.
  const frame = opts.selected
    ? 'frame-inset flex flex-col gap-1.5 p-2 ring-1 ring-gold/70'
    : 'frame-inset flex flex-col gap-1.5 p-2';
  const ownedLine = opts.owned > 0
    ? `<p class="text-[11px] text-gold">${esc(opts.selected
      ? t('{owned} owned · burning next run', { owned: opts.owned })
      : t('{owned} owned', { owned: opts.owned }))}</p>`
    : `<p class="text-[11px] text-text-dim">${esc(t('None owned'))}</p>`;
  // The Select control only exists on a card you can actually select, and is
  // absent (not disabled) on the one already chosen: a button that does
  // nothing is a button that reads as broken.
  const select = opts.owned > 0 && !opts.selected
    ? `<button data-select-rite="${esc(def.id)}" class="btn btn-stone w-full px-2 text-[12px]">${esc(t('Burn this next run'))}</button>`
    : '';
  return `
    <div class="${frame}">
      ${itemTileMarkup({
        src: riteIconSrc(def.id),
        fallbackSrc: UNKNOWN_PLATE_SRC,
        alt: t(def.name),
        muted: !opts.affordable && opts.owned === 0,
        rarity: opts.selected ? 'RARE' : 'COMMON',
        badge: opts.owned > 0 ? String(opts.owned) : undefined,
        detailAttr: `data-detail-rite="${esc(def.id)}"`,
      })}
      <div class="min-w-0">
        <p class="text-[13px] truncate">${esc(t(def.name))}</p>
        <p class="text-[11px] text-text-dim line-clamp-2">${esc(opts.effectLine)}</p>
        ${ownedLine}
      </div>
      ${select}
      ${priceButton('data-buy-rite', def.id, amountMarkup('wax', def.priceWax), opts.affordable)}
    </div>
  `;
}

/**
 * An energy bundle.
 *
 * Shows the per-unit price as well as the total, because that is the number
 * that makes a bundle a bundle — and because `energy-tuning.ts` leans on
 * those per-unit prices staying above what a run yields in Wax. A player who
 * can see them can also see that the big one is the better deal, which is the
 * honest version of upselling.
 */
export function energyBundleCardMarkup(
  bundle: { id: string; name: string; energy: number; priceEmber: number },
  affordable: boolean,
): string {
  const perUnit = (Math.round((bundle.priceEmber / bundle.energy) * 10) / 10).toLocaleString(getIntlLocale());
  return `
    <div class="frame-inset flex flex-col gap-1.5 p-2">
      <div class="relative aspect-square rounded-lg border-2 border-gold/70 bg-[radial-gradient(circle_at_50%_35%,rgba(224,166,64,0.22),transparent_70%)] grid place-items-center">
        <span class="text-[3.2rem] leading-none ${affordable ? '' : 'opacity-50 grayscale'}">${currencyIcon('energy', 1)}</span>
        <span class="chip absolute bottom-1 right-1 tabular-nums">+${groupNumber(bundle.energy)}</span>
      </div>
      <div class="min-w-0">
        <p class="text-[13px] truncate">${esc(t(bundle.name))}</p>
        <p class="text-[11px] text-text-dim">${esc(t('{price} Ember each', { price: perUnit }))}</p>
      </div>
      ${priceButton('data-buy-energy', bundle.id, amountMarkup('ember', bundle.priceEmber), affordable)}
    </div>
  `;
}

/**
 * The Ember tab: bundles, and a custom amount.
 *
 * User: "with in-game currencies we need to have bundles and custom option
 * ... then for ingame currency you can purchase all other things in game."
 *
 * ## Why both, and what each is for
 *
 * The bundles are not deals — every tier converts at the same rate as the
 * slider, because a volume discount would make spending more at once an input
 * to power (see `config/ember-packs.ts` on the backend for the full argument).
 * What a bundle buys you is not having to type a number.
 *
 * The custom field is the one that earns its place. Ember prices are specific
 * — a skin is 250, an emote 100, an energy bundle 300 — so a player who wants
 * exactly one thing should not have to buy a tier and carry the remainder.
 *
 * ## The prices come down the wire
 *
 * The card prints `pack.ember` rather than multiplying anything. Only the
 * live slider preview does arithmetic, off the server's own `emberPerEur`,
 * and the checkout it opens is priced server-side regardless — so the worst a
 * wrong preview can do is mislead, never overcharge.
 */

/**
 * `€5.00`, `€1,000.00`.
 *
 * Always two decimals, and grouped once the tiers reach four figures —
 * `€1000.00` and `€100.00` are one glance apart otherwise, which is not a
 * mistake a price list should make possible. Written in the interface
 * language (2026-10-16, i18n): `5,00 €` in German. English resolves to en-GB,
 * which prints the same as the old pinned en-US, so tests do not move with the
 * machine running them.
 */
export function formatEur(cents: number): string {
  return (cents / 100).toLocaleString(getIntlLocale(), {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

/** `4,600` — grouped, because five-figure Ember amounts are the point of the big tiers. */
export function groupNumber(value: number): string {
  return value.toLocaleString(getIntlLocale());
}

/**
 * What a custom amount buys, as the preview prints it.
 *
 * Exported because the hub updates that line in place while the field is
 * being typed into, rather than re-rendering the tab — and the two must not
 * be two different sentences. See `emberCustomMarkup` on why the client is
 * allowed to do this arithmetic at all.
 */
export function emberPreviewText(emberPerEur: number, cents: number): string {
  return t('{amount} Ember', { amount: groupNumber(Math.round((cents / 100) * emberPerEur)) });
}

/**
 * One preset.
 *
 * The price leads and the Ember follows, which is the opposite of how the
 * balance plates read but the right way round here: the player is choosing
 * how much to spend, and the Ember is the consequence.
 */
export function emberPackCardMarkup(pack: { id: string; eurCents: number; ember: number }): string {
  return `
    <div class="frame-inset flex flex-col items-center gap-1.5 p-3 text-center">
      <span class="text-[2.4rem] leading-none">${currencyIcon('ember', 1)}</span>
      <p class="font-heading text-lg">${formatEur(pack.eurCents)}</p>
      <p class="text-[13px] text-purple-light tabular-nums">${esc(t('{amount} Ember', { amount: groupNumber(pack.ember) }))}</p>
      <button data-buy-ember="${pack.eurCents}" data-pack-id="${pack.id}" class="btn btn-gold w-full mt-0.5">${esc(t('Buy'))}</button>
    </div>`;
}

/**
 * The custom row.
 *
 * A slider and a field over the same value, because the two answer different
 * questions: the slider is for "roughly this much", the field for "exactly
 * €5.00, which is what the skin costs". Both are bounded by what the server
 * sent, and the server checks the bounds again on the way in.
 */
/** The one clamp, shared by the markup and by the hub's live handlers. */
export function clampCents(cents: number, bounds: { minCents: number; maxCents: number }): number {
  return Math.min(Math.max(Math.round(cents), bounds.minCents), bounds.maxCents);
}

export function emberCustomMarkup(offers: {
  emberPerEur: number;
  custom: { minCents: number; maxCents: number; stepCents: number };
}, cents: number): string {
  const { minCents, maxCents, stepCents } = offers.custom;
  const clamped = clampCents(cents, offers.custom);
  return `
    <div class="flex items-baseline justify-between gap-3 mb-2">
      <p class="text-[13px]">${esc(t('Custom amount'))}</p>
      <p class="text-[12px] text-text-dim tabular-nums">${formatEur(minCents)} &ndash; ${formatEur(maxCents)}</p>
    </div>
    <div class="flex items-center gap-3">
      <input id="hub-ember-range" type="range" class="settings-range flex-1"
        min="${minCents}" max="${maxCents}" step="${stepCents}" value="${clamped}"
        aria-label="${esc(t('Custom amount in euros'))}" />
      <label class="flex items-center gap-1.5 shrink-0">
        <span class="text-text-dim">&euro;</span>
        <input id="hub-ember-amount" type="number" inputmode="decimal"
          min="${(minCents / 100).toFixed(2)}" max="${(maxCents / 100).toFixed(2)}" step="0.01"
          value="${(clamped / 100).toFixed(2)}" aria-label="${esc(t('Custom amount in euros'))}"
          class="w-28 min-h-11 px-2.5 text-right tabular-nums bg-surface-2 text-text border border-border rounded outline-none focus:border-gold" />
      </label>
    </div>
    <div class="frame-inset flex flex-wrap items-center justify-between gap-3 mt-3 p-3">
      <div class="min-w-0">
        <p class="text-[12px] text-text-dim">${esc(t('You receive'))}</p>
        <p id="hub-ember-preview" class="font-heading text-xl text-purple-light tabular-nums">${emberPreviewText(offers.emberPerEur, clamped)}</p>
        <p class="text-[11px] text-text-dim tabular-nums">${t('{n} Ember per &euro;1, every amount', { n: groupNumber(offers.emberPerEur) })}</p>
      </div>
      <button id="hub-ember-buy-custom" data-buy-ember="${clamped}" class="btn btn-gold px-4 shrink-0">
        ${esc(t('Buy {price}', { price: formatEur(clamped) }))}
      </button>
    </div>`;
}

/**
 * The whole tab.
 *
 * The exchange note at the bottom is not fine print. Ember converts into Wax
 * — the currency that buys gear — at a hard daily cap, which means a large
 * purchase does not become power any faster than a small one. That is the
 * single most important thing about what this shop sells, and a player is
 * entitled to know it before paying rather than after.
 */
export function emberStorefrontMarkup(offers: {
  purchasable: boolean;
  emberPerEur: number;
  packs: Array<{ id: string; eurCents: number; ember: number }>;
  custom: { minCents: number; maxCents: number; stepCents: number };
  dailyExchangeCapPremium: number;
}, customCents: number): string {
  if (!offers.purchasable) return emberUnavailableMarkup();
  return `
    <div id="hub-ember-currency" class="mb-3"></div>
    <div id="hub-ember-methods" class="mb-3"></div>
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
      ${offers.packs.map(emberPackCardMarkup).join('')}
    </div>
    <div class="frame-pewter mt-3 p-3.5">
      ${emberCustomMarkup(offers, customCents)}
    </div>
    <p class="text-[12px] text-text-dim mt-3">${esc(t('Ember buys cosmetics and energy outright. It also exchanges into Wax — but only {cap} Ember a day, however much you hold, so a bigger purchase never becomes gear any faster than a smaller one.', { cap: groupNumber(offers.dailyExchangeCapPremium) }))}</p>`;
}

/**
 * When nothing can be sold.
 *
 * Ember has exactly one faucet — real-money purchase, no other source,
 * deliberately, per design/gdd/economy-currency.md Core Rule #8 — so a
 * deployment with no payment provider registered has literally nothing to
 * offer here. Written as an empty state rather than a greyed-out storefront:
 * disabled price buttons say "you cannot afford these" when the truth is
 * "these do not exist".
 */
export function emberUnavailableMarkup(): string {
  const spends: Array<[string, string]> = [
    [t('Cosmetics'), t('Skins and emotes. Ember only, and purely how you look.')],
    [t('Energy'), t('Extra runs beyond the bar the day gives you back.')],
    [t('Exchange'), t('Turned into Wax at a fixed rate, capped per day.')],
  ];
  const rows = spends.map(([name, blurb]) => `
    <div class="frame-inset p-2.5">
      <p class="text-[13px] text-purple-light">${esc(name)}</p>
      <p class="text-[12px] text-text-dim mt-0.5">${esc(blurb)}</p>
    </div>`).join('');
  return `
    <div class="frame-inset flex flex-col items-center gap-2 p-5 text-center">
      <span class="text-[3.4rem] leading-none opacity-70">${currencyIcon('ember', 1)}</span>
      <p class="font-heading text-purple-light">${esc(t('Ember purchases are not open yet'))}</p>
      <p class="text-[12px] text-text-dim max-w-[38ch]">${esc(t('Ember is bought with real money and nothing else — it is never dropped, never earned in a run, and Wax never turns back into it. There is no checkout in this build, so the only Ember an account holds is what it was granted.'))}</p>
    </div>
    <p class="text-[12px] text-text-dim mt-3 mb-2">${esc(t('What Ember is for'))}</p>
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">${rows}</div>
  `;
}

/**
 * A Wax bundle — Ember in, Wax out.
 *
 * This shelf replaced the Exchange tab's free-form "type an Ember amount"
 * field. The economy behind it did not change: same rate, same rolling daily
 * cap, same ledger tag (see `config/wax-bundles.ts`). What changed is that the
 * amounts are now things you can look at and compare, which a number field
 * never let you do.
 *
 * `blocked` is the cap, not the wallet. A bundle you cannot afford in Ember
 * says so through the usual unaffordable price button; a bundle the daily cap
 * forbids is a different refusal and deserves its own words, because waiting
 * is the fix rather than buying more Ember.
 */
export function waxBundleCardMarkup(
  bundle: { id: string; name: string; priceEmber: number; wax: number },
  state: { affordable: boolean; blocked: boolean },
): string {
  const dim = state.blocked ? ' opacity-60' : '';
  return `
    <div class="frame-inset flex flex-col items-center gap-1.5 p-3 text-center${dim}">
      <span class="text-[2.4rem] leading-none">${currencyIcon('wax', 1)}</span>
      <p class="font-heading text-lg text-gold tabular-nums">${groupNumber(bundle.wax)}</p>
      <p class="text-[11px] text-text-dim">${esc(t(bundle.name))}</p>
      ${state.blocked
        ? `<p class="text-[11px] text-error mt-0.5">${esc(t("Past today's limit"))}</p>`
        : priceButton('data-buy-wax', bundle.id, amountMarkup('ember', bundle.priceEmber), state.affordable)}
    </div>`;
}

/**
 * The line under the shelf that explains the ceiling.
 *
 * It is stated as what is left rather than as what the rule is, because
 * "1,200 Wax left today" answers the question a player actually has. The rule
 * itself follows, once, so the number does not look arbitrary.
 */
export function waxCapLineMarkup(shelf: {
  exchangeRate: number; dailyCapPremium: number; remainingToday: number;
}): string {
  const waxLeft = shelf.remainingToday * shelf.exchangeRate;
  const spent = shelf.remainingToday <= 0;
  return `<p class="text-[12px] ${spent ? 'text-error' : 'text-text-dim'} mt-3">`
    + esc(spent
      ? t("You have used today's full allowance. It rolls off {cap} Ember at a time, twenty-four hours after each purchase.",
        { cap: groupNumber(shelf.dailyCapPremium) })
      : t('{wax} Wax left today. Wax is earned by delving; buying it is capped at {cap} Ember a rolling day, so no amount of Ember turns into gear quickly.',
        { wax: groupNumber(waxLeft), cap: groupNumber(shelf.dailyCapPremium) }))
    + '</p>';
}

/**
 * A labelled divider inside a tab, so skins and emotes read as two shelves in
 * one room — with an optional line saying what buying from this shelf *means*.
 *
 * The note exists because of a real misreading (2026-09-07). A player asked
 * for the emote cards to state "that emotes can be used 5 times and then
 * disappear" — which is not true of emotes at all. It is very nearly true of
 * the **satchel** shelf two tabs over, where everything is bought in charges,
 * spent in a run and gone. Two shelves in one shop, one selling consumables
 * and one selling permanent unlocks, and neither said which it was: the
 * consumable card counts down ("3 owned · taking 2") and the emote card said
 * only "Owned", so the countable model was the one that generalised.
 *
 * A shelf note is the right home for it rather than a card: the terms are the
 * same for every item on the shelf, and repeating them per card would spend
 * the one line each card has on something that never varies.
 */
export function shelfHeadingMarkup(text: string, note?: string): string {
  const heading = `<p class="eyebrow mt-1 ${rarityTextClass('RARE')}">${esc(text)}</p>`;
  const sub = note ? `<p class="text-[11px] text-text-dim mb-2 max-w-[52ch]">${esc(note)}</p>` : '';
  return note ? heading + sub : `${heading.replace('mt-1 ', 'mt-1 mb-2 ')}`;
}

/**
 * What a satchel item actually does, in one line.
 *
 * Moved out of the hub and given a test on 2026-09-04, after the Cinderwater
 * Draught shipped for an afternoon reading only "1.5s channel": the switch
 * had a `default` that quietly returned the channel alone, so a new effect
 * kind produced a card that described nothing. The fallback is still here —
 * a card must render for an effect this client has not been taught — but
 * `shop.test.ts` now asserts every kind the API can send has a real line.
 */
export function consumableEffectLine(def: {
  effect: ConsumableEffect; magnitude: number; durationMs: number; channelMs: number;
}, opts: { withChannel?: boolean } = {}): string {
  // The card has one line for the whole item, so the channel rides along on
  // it. The detail sheet gives the channel a row of its own, and printing it
  // twice, two rows apart, reads as a rendering bug rather than as emphasis.
  const withChannel = opts.withChannel ?? true;
  const num = (n: number) => n.toLocaleString(getIntlLocale());
  const channel = t('{s}s channel', { s: num(def.channelMs / 1000) });
  const tail = withChannel ? ` \u00b7 ${channel}` : '';
  switch (def.effect) {
    case 'HEAL_OVER_TIME':
      return t('+{n} HP over {s}s', { n: num(def.magnitude), s: num(def.durationMs / 1000) }) + tail;
    case 'RESOURCE_RESTORE':
      return tp(def.magnitude, '+{n} resource', '+{n} resource', { n: num(def.magnitude) }) + tail;
    case 'STAMINA_RESTORE':
      return t('+{n} stamina', { n: num(def.magnitude) }) + tail;
    case 'WEAPON_EDGE':
      return t('+{pct}% damage for {s}s', { pct: Math.round((def.magnitude - 1) * 100), s: num(def.durationMs / 1000) }) + tail;
    default:
      return channel;
  }
}

/**
 * A tier-1 gear piece on the Shop's Gear shelf.
 *
 * **Only tier 1 is ever here.** The ladder's upper rungs are found, and that
 * is the whole reason this shelf can exist at all: Ember converts to Wax at a
 * capped rate, so a shelf selling tier 3 would be selling the deep floors for
 * money. Selling the bottom rung costs the game nothing it was not already
 * giving away, and it means a new delver can walk in wearing something.
 *
 * The card says which floor the rest of the line drops on, because the
 * interesting thing about a starter piece is what it is the start *of*.
 */
/**
 * The Shop's Arms shelf (2026-09-04).
 *
 * Arms were buyable long before this and only from the Inventory paperdoll —
 * you had to open a weapon slot to discover that a shelf existed at all. The
 * Shop already sold worn gear from a Gear tab, so the asymmetry was not a
 * decision; a player looking for something to spend Wax on simply never found
 * ten of the things they could spend it on.
 *
 * Deliberately the same card as the gear shelf, and deliberately carrying the
 * full stat line: paid arms share their numbers with a free piece, and the
 * only honest way to show that is to print the numbers where they can be
 * compared. `blurb` sits under them for the same reason the picker shows it.
 */
export function armsShelfCardMarkup(
  row: {
    id: string; name: string; slot: string; hands?: 1 | 2; weightKg: number;
    priceWax?: number; buybackWax?: number; owned: boolean; statLine: string; blurb: string;
    /** Which wallet this arm is quoted in — the Chandlery band takes Ember, the rest Wax. */
    currency?: 'SOFT' | 'PREMIUM';
    /** Already in this delver's hand — the card says so instead of offering to equip it again. */
    equipped?: boolean;
  },
  affordable: boolean,
): string {
  // The coin follows the piece (2026-09-07), exactly as it does on the gear
  // card. User: "We have no arms for ember. We have to."
  const coin = row.currency === 'PREMIUM' ? 'ember' : 'wax';
  // Three states, not two (2026-09-06). User: "from here can we list all items
  // in the shop? I see less gear in shop then in inventory."
  //
  // The shelf used to list only the eighteen priced arms, so the thirteen every
  // delver starts with — the ones filling half the picker — had no card at all.
  // They are listed now, and they need their own state: a starter piece has no
  // price and no buyback, so the owned branch would have drawn "Sell 0 Wax",
  // which reads as broken and, if it ever paid, would be a Wax faucet out of
  // nothing.
  const free = row.priceWax === undefined;
  // An owned arm equips from the shelf as well as selling, the same as a worn
  // piece (2026-09-06). User: *"All other in shop, you can buy and equip."*
  // The gear card got this and the arms card did not, which a live check
  // caught: 22 Equip buttons on the Gear tab and 0 on Arms.
  const equipAndSell = (sellLabel: string): string => `
    <div class="${EQUIP_SELL_ROW}">
      <button data-equip-arms-shop="${esc(row.id)}" class="btn ${row.equipped ? 'btn-stone-on' : 'btn-stone'} flex-1 px-2 text-[12px]"
        ${row.equipped ? 'disabled' : ''}>${esc(row.equipped ? t('Held') : t('Equip'))}</button>
      ${sellLabel}
    </div>`;
  const action = free
    ? equipAndSell(`<p class="flex-1 text-[12px] text-center text-text-dim/70 py-1.5">${esc(t('Yours'))}</p>`)
    : row.owned
      ? equipAndSell(sellButton('data-sell-arms', row.id, amountMarkup(coin, row.buybackWax ?? 0, { hideLabel: true }),
        { title: coin === 'ember'
          ? t('Sell back for {refund} Ember. You can buy it again for {price}.', {
            refund: groupNumber(row.buybackWax ?? 0), price: groupNumber(row.priceWax ?? 0) })
          : t('Sell back for {refund} Wax. You can buy it again for {price}.', {
            refund: groupNumber(row.buybackWax ?? 0), price: groupNumber(row.priceWax ?? 0) }) }))
      : priceButton('data-buy-arms-shop', row.id, amountMarkup(coin, row.priceWax ?? 0), affordable);
  // `data-shelf-item` on the card, not just on its button: a free arm has no
  // button at all, so without this there is no way for a test — or for the
  // check that the shop and the inventory list the same items — to see that
  // the card is there.
  return `
    <div data-shelf-item="${esc(row.id)}" class="frame-inset flex h-full flex-col gap-1.5 p-2">
      ${itemTileMarkup({
    src: `/assets/ui/gear/${esc(row.id)}.webp`,
    alt: t(row.name),
    muted: !row.owned && !affordable,
  })}
      <div class="min-w-0">
        <p class="text-[13px] truncate">${esc(t(row.name))}</p>
        <p class="text-[11px] text-text-dim truncate">${esc(row.statLine)}</p>
        <p class="text-[11px] text-text-dim/75 truncate">${esc(t(row.slot))} &middot; ${esc(t('{kg}kg', { kg: row.weightKg.toLocaleString(getIntlLocale()) }))}</p>
        <p class="text-[11px] text-text-dim/60 line-clamp-2">${esc(t(row.blurb))}</p>
      </div>
      <div class="mt-auto">${action}</div>
    </div>`;
}


/**
 * What the tooltip says before you press Sell.
 *
 * The two cases are genuinely different and the copy has to say so: a tier-1
 * piece can be bought again at a known price, and a deeper one cannot be
 * bought again at all. Calling both of them "sell" without that distinction
 * would make the irreversible one feel like the reversible one.
 */
const SELL_GONE: Record<string, string> = {
  Cellars: N_('Sell back for {refund} Wax. Nothing sells it back to you — the only other one is in the Cellars.'),
  Ossuary: N_('Sell back for {refund} Wax. Nothing sells it back to you — the only other one is in the Ossuary.'),
  Vaults: N_('Sell back for {refund} Wax. Nothing sells it back to you — the only other one is in the Vaults.'),
};

export function sellHint(row: { priceWax?: number; buybackWax?: number; tierName?: string }): string {
  const paid = row.buybackWax ?? 0;
  // One sentence per floor (i18n): a floor name injected after "in the" cannot
  // be declined. `tierName` is the server's TIER_NAME — Cellars/Ossuary/Vaults.
  const gone = row.tierName && Object.hasOwn(SELL_GONE, row.tierName) ? SELL_GONE[row.tierName] : undefined;
  return row.priceWax === undefined
    ? t(gone ?? N_('Sell back for {refund} Wax. Nothing sells it back to you — the only other one is in the deep.'), {
      refund: groupNumber(paid) })
    : t('Sell back for {refund} Wax. You can buy it again for {price}.', {
      refund: groupNumber(paid), price: groupNumber(row.priceWax) });
}

export function gearShelfCardMarkup(
  row: {
    id: string; name: string; slot: string; line: string; weightKg: number;
    priceWax?: number; buybackWax?: number; owned: boolean; statLine: string; tierName?: string;
    /** Which wallet this rung is quoted in — Cellars are Wax, the deep floors Ember. */
    currency?: 'SOFT' | 'PREMIUM';
    equipped?: boolean;
  },
  affordable: boolean,
): string {
  const coin = row.currency === 'PREMIUM' ? 'ember' : 'wax';
  // Owned pieces gain Equip beside Sell (2026-09-06). User: *"All other in
  // shop, you can buy and equip."* The picker stopped selling on the same day,
  // so this is the other half of that move: the shelf is where a piece is
  // bought AND put on, and the inventory is the wardrobe.
  const action = row.owned
    ? `<div class="${EQUIP_SELL_ROW}">
         <button data-equip-gear-shop="${esc(row.id)}" class="btn ${row.equipped ? 'btn-stone-on' : 'btn-stone'} flex-1 px-2 text-[12px]"
           ${row.equipped ? 'disabled' : ''}>${esc(row.equipped ? t('Worn') : t('Equip'))}</button>
         ${sellButton('data-sell-gear', row.id, amountMarkup(coin, row.buybackWax ?? 0, { hideLabel: true }),
    { title: sellHint(row) })}
       </div>`
    : priceButton('data-buy-gear', row.id, amountMarkup(coin, row.priceWax ?? 0), affordable);
  return `
    <div data-shelf-item="${esc(row.id)}" class="frame-inset flex flex-col gap-1.5 p-2">
      ${itemTileMarkup({
    src: `/assets/ui/worn/${esc(row.id)}.webp`,
    alt: t(row.name),
    muted: !row.owned && !affordable,
  })}
      <div class="min-w-0">
        <p class="text-[13px] truncate">${esc(t(row.name))}</p>
        <p class="text-[11px] text-text-dim truncate">${esc(row.statLine)}</p>
        <p class="text-[11px] text-text-dim/75 truncate">${esc(t(row.slot))} &middot; ${esc(t(row.line))} &middot; ${esc(t('{kg}kg', { kg: row.weightKg.toLocaleString(getIntlLocale()) }))}</p>
      </div>
      ${action}
    </div>`;
}
